import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Admin.css';

function AdminVideos({ onLogout }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSyncForm, setShowSyncForm] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    youtube_url: '',
    description: '',
    featured_on_homepage: false,
    display_on_videos_page: true
  });
  const [syncData, setSyncData] = useState({
    sync_type: 'playlist',  // 'playlist' or 'channel'
    id: '',
    max_results: 20
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/videos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setVideos(data.videos);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('adminToken');
      const url = editingVideo
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/videos/${editingVideo.video_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/videos`;
      
      const response = await fetch(url, {
        method: editingVideo ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowForm(false);
        setEditingVideo(null);
        setFormData({
          title: '',
          youtube_url: '',
          description: '',
          featured_on_homepage: false,
          display_on_videos_page: true
        });
        fetchVideos();
        alert('Video saved successfully!');
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to save video'}`);
      }
    } catch (error) {
      alert('Error saving video');
    }
  };

  const handleSync = async (e) => {
    e.preventDefault();
    setSyncing(true);

    try {
      const token = localStorage.getItem('adminToken');
      const endpoint = syncData.sync_type === 'playlist' 
        ? '/api/admin/videos/sync/playlist'
        : '/api/admin/videos/sync/channel';
      
      const payload = syncData.sync_type === 'playlist'
        ? { playlist_id: syncData.id, max_results: syncData.max_results, auto_display: true }
        : { channel_id: syncData.id, max_results: syncData.max_results, auto_display: true };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ Sync complete!\n\n• Added: ${result.added} videos\n• Skipped (duplicates): ${result.skipped}\n• Total fetched: ${result.total_fetched}`);
        setShowSyncForm(false);
        setSyncData({ sync_type: 'playlist', id: '', max_results: 20 });
        fetchVideos();
      } else {
        const error = await response.json();
        alert(`❌ Sync failed:\n${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      alert(`❌ Sync error: ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      youtube_url: video.youtube_url,
      description: video.description || '',
      featured_on_homepage: video.featured_on_homepage,
      display_on_videos_page: video.display_on_videos_page
    });
    setShowForm(true);
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/videos/${videoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchVideos();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingVideo(null);
    setFormData({
      title: '',
      youtube_url: '',
      description: '',
      featured_on_homepage: false,
      display_on_videos_page: true
    });
  };

  return (
    <div className="admin-page" data-testid="admin-videos">
      <nav className="admin-nav">
        <div className="admin-nav-content">
          <h1 className="brand-text">ClutchBabe9 Admin</h1>
          <div className="admin-nav-links">
            <Link to="/admin/dashboard" className="admin-nav-link">Dashboard</Link>
            <Link to="/admin/posts" className="admin-nav-link">Posts</Link>
            <Link to="/admin/videos" className="admin-nav-link active">Videos</Link>
            <Link to="/admin/subscribers" className="admin-nav-link">Subscribers</Link>
            <Link to="/admin/contacts" className="admin-nav-link">Contacts</Link>
            <Link to="/admin/profile" className="admin-nav-link">Profile</Link>
            <Link to="/admin/config" className="admin-nav-link">Settings</Link>
            <Link to="/" className="admin-nav-link view-site-link" data-testid="view-site-btn">🌐 View Site</Link>
            <button onClick={() => { onLogout(); navigate('/admin/login'); }} className="btn btn-ghost">Logout</button>
          </div>
        </div>
      </nav>

      <div className="admin-content">
        <div className="admin-header">
          <h2>Manage Videos</h2>
          <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
            {!showForm && !showSyncForm && (
              <>
                <button 
                  onClick={() => setShowSyncForm(true)} 
                  className="btn btn-secondary"
                  data-testid="sync-youtube-btn"
                >
                  🔄 Sync from YouTube
                </button>
                <button 
                  onClick={() => setShowForm(true)} 
                  className="btn btn-primary" 
                  data-testid="add-video-btn"
                >
                  + Add Video
                </button>
              </>
            )}
          </div>
        </div>

        {/* YouTube Sync Form */}
        {showSyncForm && (
          <div className="video-form glass-card" style={{ marginBottom: 'var(--spacing-xl)' }}>
            <h3>🔄 Sync Videos from YouTube</h3>
            <p style={{ color: 'var(--text-muted)', marginBottom: 'var(--spacing-lg)' }}>
              Auto-import videos from your YouTube channel or a specific playlist.
            </p>
            
            <form onSubmit={handleSync}>
              <div className="form-group">
                <label>Sync Method</label>
                <select
                  value={syncData.sync_type}
                  onChange={(e) => setSyncData({ ...syncData, sync_type: e.target.value })}
                  data-testid="sync-type-select"
                >
                  <option value="playlist">📋 From Playlist (Recommended)</option>
                  <option value="channel">📺 From Channel (All Uploads)</option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  {syncData.sync_type === 'playlist' ? 'YouTube Playlist ID' : 'YouTube Channel ID'} *
                </label>
                <input
                  type="text"
                  value={syncData.id}
                  onChange={(e) => setSyncData({ ...syncData, id: e.target.value })}
                  placeholder={
                    syncData.sync_type === 'playlist' 
                      ? 'PLxxxxxxxxxxxxxxxxxxxxxx'
                      : 'UCxxxxxxxxxxxxxxxxxxxxxx'
                  }
                  required
                  data-testid="sync-id-input"
                />
                <small style={{ color: 'var(--text-muted)', display: 'block', marginTop: '0.5rem' }}>
                  {syncData.sync_type === 'playlist' ? (
                    <>
                      Find it in the playlist URL: youtube.com/playlist?list=<strong>PLxxxxx</strong>
                    </>
                  ) : (
                    <>
                      Find it in your channel URL: youtube.com/channel/<strong>UCxxxxx</strong>
                    </>
                  )}
                </small>
              </div>

              <div className="form-group">
                <label>Max Videos to Import</label>
                <input
                  type="number"
                  value={syncData.max_results}
                  onChange={(e) => setSyncData({ ...syncData, max_results: parseInt(e.target.value) })}
                  min="1"
                  max="50"
                  data-testid="sync-max-results-input"
                />
                <small style={{ color: 'var(--text-muted)', display: 'block', marginTop: '0.5rem' }}>
                  Maximum: 50 videos per sync
                </small>
              </div>

              <div className="form-actions" style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  disabled={syncing}
                  data-testid="start-sync-btn"
                >
                  {syncing ? '⏳ Syncing...' : '🚀 Start Sync'}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowSyncForm(false);
                    setSyncData({ sync_type: 'playlist', id: '', max_results: 20 });
                  }} 
                  className="btn btn-secondary"
                  data-testid="cancel-sync-btn"
                >
                  Cancel
                </button>
              </div>

              <div style={{
                marginTop: 'var(--spacing-lg)',
                padding: 'var(--spacing-md)',
                background: 'rgba(0, 217, 255, 0.1)',
                border: '1px solid rgba(0, 217, 255, 0.3)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.875rem'
              }}>
                <strong>💡 Tip:</strong> Using a playlist gives you more control over which videos appear on your site. Create a "Featured" playlist and sync from there!
              </div>
            </form>
          </div>
        )}

        {/* Manual Add/Edit Form */}
        {showForm && (
          <div className="video-form glass-card" style={{ marginBottom: 'var(--spacing-xl)' }}>
            <h3>{editingVideo ? 'Edit Video' : 'Add New Video'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Video Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter video title"
                  required
                  data-testid="video-title-input"
                />
              </div>

              <div className="form-group">
                <label>YouTube URL *</label>
                <input
                  type="text"
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  placeholder="https://youtu.be/... or https://youtube.com/watch?v=... or https://youtube.com/shorts/..."
                  required
                  data-testid="video-url-input"
                />
                <small style={{ color: 'var(--text-muted)', display: 'block', marginTop: '0.5rem' }}>
                  ✅ Accepts: youtube.com/watch?v=VIDEO_ID, youtu.be/VIDEO_ID, youtube.com/shorts/VIDEO_ID
                </small>
              </div>

              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  placeholder="Brief description of the video..."
                  data-testid="video-description-input"
                />
              </div>

              <div className="form-group" style={{ display: 'flex', gap: 'var(--spacing-lg)' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.featured_on_homepage}
                    onChange={(e) => setFormData({ ...formData, featured_on_homepage: e.target.checked })}
                    style={{ marginRight: '0.5rem' }}
                    data-testid="video-featured-checkbox"
                  />
                  Feature on Homepage
                </label>

                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.display_on_videos_page}
                    onChange={(e) => setFormData({ ...formData, display_on_videos_page: e.target.checked })}
                    style={{ marginRight: '0.5rem' }}
                    data-testid="video-display-checkbox"
                  />
                  Display on Videos Page
                </label>
              </div>

              <div className="form-actions" style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
                <button type="submit" className="btn btn-primary" data-testid="save-video-btn">
                  {editingVideo ? 'Update Video' : 'Add Video'}
                </button>
                <button type="button" onClick={handleCancel} className="btn btn-secondary" data-testid="cancel-video-btn">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="loading-spinner"></div>
        ) : videos.length === 0 ? (
          <div className="empty-state glass-card" data-testid="videos-empty-state">
            <div className="empty-icon">🎬</div>
            <h3>No videos yet</h3>
            <p>Add your first video manually or sync from YouTube!</p>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>YouTube ID</th>
                  <th>Featured</th>
                  <th>On Videos Page</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {videos.map(video => (
                  <tr key={video.video_id} data-testid={`video-row-${video.video_id}`}>
                    <td>{video.title}</td>
                    <td>
                      <code style={{ fontSize: '0.85rem', color: 'var(--electric-blue)' }}>
                        {video.youtube_id}
                      </code>
                    </td>
                    <td>{video.featured_on_homepage ? '⭐' : ''}</td>
                    <td>{video.display_on_videos_page ? '✅' : '❌'}</td>
                    <td>
                      <div className="table-actions">
                        <button 
                          onClick={() => handleEdit(video)} 
                          className="btn-sm btn-secondary"
                          data-testid={`edit-video-${video.video_id}`}
                        >
                          Edit
                        </button>
                        <a
                          href={video.youtube_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-sm btn-primary"
                          data-testid={`view-video-${video.video_id}`}
                        >
                          View
                        </a>
                        <button 
                          onClick={() => handleDelete(video.video_id)} 
                          className="btn-sm btn-danger"
                          data-testid={`delete-video-${video.video_id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminVideos;
